import { useRef, useMemo } from "react";
import { Bold, Heading2, Link as LinkIcon, List, Table as TableIcon, Eye, Pencil } from "lucide-react";

/**
 * Markdown-lite WYSIWYG editor.
 * The blog renderer supports: ## headings, **bold**, tables ( | col1 | col2 |\n|---|---|\n... ),
 * plain paragraphs. This editor exposes toolbar buttons that wrap the selected text in the
 * relevant syntax, plus a live preview so the author sees exactly what will ship.
 */
function renderPreview(content) {
  if (!content) return [];
  const lines = content.split("\n");
  const out = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("## ")) {
      out.push(<h2 key={k++} className="mt-6 text-xl font-display font-bold text-[#0A0D14]">{line.slice(3)}</h2>);
      i++; continue;
    }
    if (line.startsWith("|") && lines[i + 1]?.includes("---")) {
      const headers = line.split("|").map((s) => s.trim()).filter(Boolean);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        rows.push(lines[i].split("|").map((s) => s.trim()).filter(Boolean));
        i++;
      }
      out.push(
        <div key={k++} className="mt-3 border border-[#E2E8F0] rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-[#F8F9FA] text-left">
              <tr>{headers.map((h, j) => <th key={j} className="px-2 py-1.5 font-medium text-[#0A0D14]">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, kk) => (
                <tr key={kk} className="border-t border-[#E2E8F0]">
                  {r.map((c, l) => <td key={l} className="px-2 py-1.5 text-[#525B6A] tabular-nums">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    if (line.trim() === "") { i++; continue; }
    const parts = line.split(/\*\*(.+?)\*\*/g);
    out.push(
      <p key={k++} className="mt-3 text-sm leading-relaxed text-[#525B6A]">
        {parts.map((p, j) => (j % 2 === 1 ? <strong key={j} className="text-[#0A0D14]">{p}</strong> : p))}
      </p>
    );
    i++;
  }
  return out;
}

function ToolbarBtn({ onClick, title, testid, children }) {
  return (
    <button type="button" onClick={onClick} title={title} data-testid={testid} className="p-1.5 rounded hover:bg-[#F8F9FA] text-[#525B6A] hover:text-[#0047FF] transition-colors">
      {children}
    </button>
  );
}

export default function MarkdownEditor({ value, onChange, minHeight = 380 }) {
  const ref = useRef(null);

  const wrap = (before, after = before) => {
    const el = ref.current;
    if (!el) return;
    const s = el.selectionStart;
    const e = el.selectionEnd;
    const selected = value.slice(s, e);
    const next = value.slice(0, s) + before + (selected || "text") + after + value.slice(e);
    onChange(next);
    setTimeout(() => {
      el.focus();
      const cursor = s + before.length + (selected ? selected.length : 4);
      el.setSelectionRange(cursor, cursor);
    }, 0);
  };

  const insertLine = (text) => {
    const el = ref.current;
    if (!el) return;
    const s = el.selectionStart;
    const start = value.lastIndexOf("\n", s - 1) + 1;
    const next = value.slice(0, start) + text + value.slice(start);
    onChange(next);
    setTimeout(() => { el.focus(); el.setSelectionRange(start + text.length, start + text.length); }, 0);
  };

  const insertBlock = (block) => {
    const el = ref.current;
    if (!el) return;
    const s = el.selectionStart;
    const next = value.slice(0, s) + block + value.slice(s);
    onChange(next);
  };

  const preview = useMemo(() => renderPreview(value || ""), [value]);

  return (
    <div className="border border-[#E2E8F0] rounded-md overflow-hidden bg-white" data-testid="markdown-editor">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-[#E2E8F0] bg-[#F8F9FA]">
        <ToolbarBtn testid="md-h2" onClick={() => insertLine("## ")} title="Heading"><Heading2 className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn testid="md-bold" onClick={() => wrap("**")} title="Bold"><Bold className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn testid="md-list" onClick={() => insertLine("- ")} title="List"><List className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn testid="md-link" onClick={() => wrap("[", "](https://)")} title="Link"><LinkIcon className="h-4 w-4" /></ToolbarBtn>
        <ToolbarBtn testid="md-table" onClick={() => insertBlock("\n| Column A | Column B |\n|---|---|\n| Value 1 | Value 2 |\n")} title="Table"><TableIcon className="h-4 w-4" /></ToolbarBtn>
        <div className="ml-auto flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#8A94A6]">
          <Pencil className="h-3 w-3" /> Editor
          <span className="mx-1">·</span>
          <Eye className="h-3 w-3" /> Live preview
        </div>
      </div>
      <div className="grid md:grid-cols-2 divide-x divide-[#E2E8F0]" style={{ minHeight }}>
        <textarea
          ref={ref}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid="md-textarea"
          className="w-full h-full min-h-[380px] p-4 text-sm font-mono text-[#0A0D14] outline-none resize-none"
          placeholder="Write your post in markdown-lite. Use the toolbar to style."
        />
        <div className="p-4 overflow-y-auto max-h-[70vh] bg-[#FBFCFD]" data-testid="md-preview">
          {preview.length === 0
            ? <div className="text-xs text-[#8A94A6] italic">Preview appears here as you type.</div>
            : preview}
        </div>
      </div>
    </div>
  );
}
